import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Modal, Level, Box, Heading, Toasts, Button, Toolbar, Icon } from '@deseretbook/react-ui'
import { DeseretBook } from '../../../dbcom/api'
import {
  AddressFormManager,
  AddressFormFields,
  PaymentFormFields,
  PaymentFormManager,
} from '../../forms'

// Translation between braintree card types and icon names.
const cardIcons = {
  'american express': 'ccAmex',
  mastercard: 'ccMastercard',
  visa: 'ccVisa',
  discover: 'ccDiscover',
}

/**
 * New Card View of the subscription signup modal.
 *
 * @returns {function} Component
 */
const NewCardView = ({ user, onSuccess, changeView }) => {
  const [addressForm, setAddressForm] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  /**
   * Updates the address form with the given changes.
   *
   * @param {object} [changes={}] - Changes to address
   */
  const updateAddress = (changes = {}) => {
    setAddressForm(prevAddress => ({ ...prevAddress, ...changes }))
  }

  /**
   * Saves the user's address to their address book.
   *
   * @param {object} hostedFields - HostedFields data
   */
  const saveAddress = (hostedFields) => {
    setIsSubmitting(true)

    hostedFields
      .tokenize(async (tokenizeError, payload) => {
        try {
          if (tokenizeError) {
            throw new Error(tokenizeError)
          }

          const newCard = await DeseretBook
            .user(user)
            .creditCard()
            .create({
              cardholderName: `${addressForm.firstname} ${addressForm.lastname}`,
              paymentMethodNonce: payload.nonce,
              billingAddress: addressForm,
            })

          setIsSubmitting(false)
          Toasts.success('Card saved')

          if (onSuccess && typeof onSuccess === 'function') {
            onSuccess(newCard)
          }
        } catch (error) {
          setIsSubmitting(false)
          Toasts.danger('Unable to save', error.message)
        }
      })
  }

  return (
    <AddressFormManager
      address={addressForm}
      onChange={updateAddress}
    >
      {/*
        TODO I think paymentMethod defaults to the Spree::Card or whatever, but we should send the
        real thing in probably
      */}
      {addressFormProps => (
        <PaymentFormManager paymentMethod={{}}>
          {(paymentFormProps) => {
            const { hostedFields, currentCard } = paymentFormProps
            const isFetchingData = addressFormProps.isFetchingData
              || paymentFormProps.isFetchingData

            return (
              <Form
                onSubmit={() => saveAddress(hostedFields)}
                isSubmitting={isSubmitting}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Modal.Header>
                  <Toolbar
                    titleContent="Create New Card"
                    figureContent={<Icon name={cardIcons[currentCard && currentCard.niceType.toLowerCase()] || 'ccGeneric'} />}
                  />
                </Modal.Header>
                <Modal.Content>
                  <PaymentFormFields {...paymentFormProps} />
                  <Box paddingVertical={1}>
                    <Heading>Billing Address</Heading>
                  </Box>
                  <AddressFormFields {...addressFormProps} />
                </Modal.Content>
                <Modal.Footer>
                  <Level>
                    <Level.Right>
                      <Level.Item>
                        <Button
                          type="button"
                          onClick={() => changeView('form')}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </Button>
                      </Level.Item>
                      <Level.Item>
                        <Form.SubmitButton
                          color="action"
                          disabled={isFetchingData}
                        >
                          Create New Card
                        </Form.SubmitButton>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                </Modal.Footer>
              </Form>
            )
          }}
        </PaymentFormManager>
      )}
    </AddressFormManager>
  )
}

NewCardView.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  onSuccess: PropTypes.func,
  changeView: PropTypes.func,
}

NewCardView.defaultProps = {
  onSuccess: () => {},
  changeView: () => {},
}

export default NewCardView
